
import React, { createContext, useContext, useEffect, useState } from 'react';

type OfflineContextType = {
  isOffline: boolean;
  setIsOffline: (offline: boolean) => void;
};

const OfflineContext = createContext<OfflineContextType>({ 
  isOffline: false,
  setIsOffline: () => {}
});

export const useOfflineStatus = () => useContext(OfflineContext);

export const OfflineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with false to avoid SSR issues, will be updated in useEffect
  let isOffline = false;
  let setIsOffline: (offline: boolean) => void = () => {};

  // Add error boundary for React hooks
  try {
    [isOffline, setIsOffline] = useState<boolean>(false);
  } catch (error) {
    console.warn('Hook error in OfflineProvider, using fallback state:', error);
    // Fallback to static values if hooks are corrupted
    isOffline = typeof window !== 'undefined' ? !navigator.onLine : false;
    setIsOffline = () => {};
  }

  useEffect(() => {
    // Function to check and update connection status
    const updateConnectionStatus = () => {
      if (typeof window !== 'undefined' && 'navigator' in window) {
        const connectionStatus = navigator.onLine;
        try {
          setIsOffline(!connectionStatus);
          console.log(`OfflineContext: Connection status updated - ${connectionStatus ? 'ONLINE' : 'OFFLINE'}`);
        } catch (error) {
          console.warn('Error updating offline status:', error);
        }
      }
    };
    
    // Set the initial state based on online status once component mounts
    updateConnectionStatus();
    
    // Set up event listeners
    const handleOnline = () => {
      console.log("OfflineContext: Device is now ONLINE");
      try {
        setIsOffline(false);
      } catch (error) {
        console.warn('Error setting online status:', error);
      }
    };
    
    const handleOffline = () => {
      console.log("OfflineContext: Device is now OFFLINE");
      try {
        setIsOffline(true);
      } catch (error) {
        console.warn('Error setting offline status:', error);
      }
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      
      // Set up a periodic check (useful for detecting network status changes)
      const intervalId = setInterval(updateConnectionStatus, 30000);
      
      // Clean up listeners on unmount
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        clearInterval(intervalId);
      };
    }
  }, []);

  return (
    <OfflineContext.Provider value={{ isOffline, setIsOffline }}>
      {children}
    </OfflineContext.Provider>
  );
};
